<script setup lang="ts">
import { ElIcon, ElPopover } from 'element-plus'
import type { CSSProperties } from 'vue'
import { computed } from 'vue'

import { BoxComponent, FlexComponent, PlatformIconComponent, TooltipComponent, TypographyComponent } from '..'
import { CopyIcon, MetapicIcon } from '../../assets'
import { formatNumber } from '../../formatters'
import { type MyPageLink, type SocialItem, SocialItemEnum } from '../../types'
import { copyTextToClipboard } from '../../utils/copy-text.utils'

const props = withDefaults(
  defineProps<{
    socialMedia: SocialItem[]
    showExtendedData?: boolean
    iconSize?: number
    myPageIconSize?: number
    showAllTypes?: boolean
    justify?: CSSProperties['justifyContent']
    myPage?: MyPageLink | null
  }>(),
  { iconSize: 24, justify: 'center' },
)

const MY_PAGE_TITLE = 'My page'

const SOCIAL_MEDIA_MAX_COUNT = 6

const SUPPORTED_SOCIAL_TYPES = new Set<SocialItemEnum>([SocialItemEnum.Instagram, SocialItemEnum.TikTok, SocialItemEnum.Snapchat])

const socialAccounts = computed(() => {
  const socialItems = props.showAllTypes ? props.socialMedia : props.socialMedia.filter((item) => SUPPORTED_SOCIAL_TYPES.has(item.type))
  const connectedAccounts = socialItems.filter((account) => account.isConnected)
  const unconnectedAccounts = socialItems.filter((account) => !account.isConnected)

  return [...connectedAccounts, ...unconnectedAccounts].slice(0, SOCIAL_MEDIA_MAX_COUNT).map((account) => ({
    ...account,
    compactFollowersCount: account.followers ? formatNumber(account.followers, 'compact') : undefined,
    fullFollowersCount: account.followers ? formatNumber(account.followers) : undefined,
  }))
})
</script>
<template>
  <FlexComponent :justify="justify" :gap="8" align="center">
    <template v-for="socialAccount in socialAccounts" :key="socialAccount.id">
      <FlexComponent :gap="socialAccount.isConnected ? 4 : 2" align="center">
        <ElPopover
          :width="200"
          placement="bottom"
          trigger="hover"
          :teleported="false"
          :show-arrow="false"
          popper-class="social-media-account__link-details-popover"
        >
          <BoxComponent :mb="4">
            <FlexComponent justify="space-between" align="center" :gap="8">
              <TypographyComponent :font-weight="600" text-transform="capitalize" variant="title-medium">
                {{ socialAccount.title }}
              </TypographyComponent>
              <TypographyComponent v-if="socialAccount.isConnected" variant="label-medium" class="social-media-account__link-details-label">
                Extended Data
              </TypographyComponent>
            </FlexComponent>
          </BoxComponent>
          <FlexComponent :gap="4" align="center">
            <TypographyComponent variant="body-medium" text-truncate>{{ socialAccount.identifier }}</TypographyComponent>
            <TooltipComponent trigger="click" :auto-close="1000">
              <ElIcon
                :size="18"
                class="social-media-account__copy-icon"
                @click.stop.prevent="copyTextToClipboard(socialAccount.identifier)"
              >
                <CopyIcon />
              </ElIcon>
              <template #content> Copied! </template>
            </TooltipComponent>
          </FlexComponent>
          <TypographyComponent
            v-if="socialAccount.followers && (socialAccount.isConnected || props.showExtendedData)"
            variant="body-medium"
          >
            {{ socialAccount.fullFollowersCount }} {{ socialAccount.followers === 1 ? 'follower' : 'followers' }}
          </TypographyComponent>
          <template #reference>
            <FlexComponent :gap="socialAccount.isConnected ? 4 : 2" align="center" class="social-media-account__details-reference">
              <a
                :href="socialAccount.link"
                target="_blank"
                rel="noopener noreferrer"
                class="social-media-account__icon-container"
                :aria-label="socialAccount.isConnected ? `${socialAccount.title} (connected)` : socialAccount.title"
                @click.stop
              >
                <PlatformIconComponent
                  :platform="socialAccount.type"
                  :size="props.iconSize"
                  :is-connected="socialAccount.isConnected"
                  aria-hidden="true"
                />
              </a>
              <TypographyComponent
                v-if="socialAccount.compactFollowersCount"
                variant="body-medium"
                :class="{ 'social-media-account__followers--unconnected': !socialAccount.isConnected }"
              >
                {{ socialAccount.compactFollowersCount }}
              </TypographyComponent>
            </FlexComponent>
          </template>
        </ElPopover>
      </FlexComponent>
    </template>
    <ElPopover
      v-if="props.myPage"
      :width="200"
      placement="bottom"
      trigger="hover"
      :teleported="false"
      :show-arrow="false"
      popper-class="social-media-account__link-details-popover"
    >
      <BoxComponent :mb="4">
        <TypographyComponent :font-weight="600" variant="title-medium">{{ MY_PAGE_TITLE }}</TypographyComponent>
      </BoxComponent>
      <FlexComponent :gap="4" align="center">
        <TypographyComponent variant="body-medium" text-truncate>{{ props.myPage.slug }}</TypographyComponent>
        <TooltipComponent trigger="click" :auto-close="1000">
          <ElIcon :size="18" class="social-media-account__copy-icon" @click.stop.prevent="copyTextToClipboard(props.myPage.slug)">
            <CopyIcon />
          </ElIcon>
          <template #content> Copied! </template>
        </TooltipComponent>
      </FlexComponent>
      <template #reference>
        <a
          :href="props.myPage.url"
          target="_blank"
          rel="noopener noreferrer"
          class="social-media-account__icon-container social-media-account__my-page"
          :style="{ minWidth: `${props.iconSize}px`, minHeight: `${props.iconSize}px` }"
          :aria-label="MY_PAGE_TITLE"
          @click.stop
        >
          <ElIcon :size="props.myPageIconSize ?? props.iconSize" color="var(--primary)">
            <MetapicIcon />
          </ElIcon>
        </a>
      </template>
    </ElPopover>
  </FlexComponent>
</template>
<style lang="scss" scoped>
.social-media-account__icon-container {
  display: inline-flex;
  color: var(--primary);
}
.social-media-account__my-page {
  align-items: center;
  justify-content: center;
}
.social-media-account__my-page :deep(.el-icon) {
  box-sizing: border-box;
  padding: 1px;
  margin: 0 1px;

  svg {
    width: 100%;
    height: 100%;
  }
}
.social-media-account__link-details-label {
  background-color: var(--primary);
  color: var(--inverted);
  border-radius: 32px;
  padding: 2px 8px;

  font-size: 11px;
  line-height: 16px;
  font-weight: 600;
}

.social-media-account__copy-icon {
  cursor: pointer;
}

/*
 * Chrome rounds Urbanist's ascent and descent to whole pixels (13.3 → 13, 3.5 → 4 at 14px), which
 * lifts the baseline by ~1px, so a centred count reads high next to a bare icon. The offset shrinks
 * with zoom, so it is most visible at 100%. A connected icon keeps the plain box: its top-right badge
 * already lifts the icon's visual centre to meet the digits.
 */
.social-media-account__followers--unconnected {
  position: relative;
  top: 1px;
}

.social-media-account__followers--hoverable {
  cursor: pointer;
}
:deep(.el-icon) {
  path {
    fill: currentColor;
  }
}
</style>
<style lang="scss">
.social-media-account__link-details-popover {
  &.el-popover {
    text-align: left;
  }
}
</style>
